import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { queryClient } from "@/config/react-query";
import { usePatientPortal } from "@/contexts/patient-portal-provider";
import { portalBenefitsQueryKeys } from "@/api/patient/portal-benefits-query";
import { cancelMembership, createPaymentMethodConfig, deletePaymentMethod, enrollInMembership, getEarlyCancellationFee, getMembershipDetail, listAvailableMemberships, listMyMemberships, listPaymentMethods, savePaymentMethod, setDefaultPaymentMethod, updateMembershipPaymentMethod } from "@/api/patient/portal-billing";
export const portalBillingQueryKeys = {
  all: ["patient-portal", "billing"] as const,
  paymentMethods: (tenantScope: string, userScope: string) => [...portalBillingQueryKeys.all, "payment-methods", tenantScope, userScope] as const,
  memberships: (userScope: string) => [...portalBillingQueryKeys.all, "memberships", userScope] as const,
  membershipDetail: (membershipId: string) => [...portalBillingQueryKeys.all, "membership", membershipId] as const,
  myMemberships: (userScope: string) => [...portalBillingQueryKeys.all, "my-memberships", userScope] as const,
  earlyCancellationFee: (memberId: string) => [...portalBillingQueryKeys.all, "early-cancellation-fee", memberId] as const
};
export function useEarlyCancellationFee(memberId, options) {
  const $ = _c(8);
  let t0;
  if ($[0] !== memberId) {
    t0 = portalBillingQueryKeys.earlyCancellationFee(memberId ?? "");
    $[0] = memberId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== memberId) {
    t1 = () => getEarlyCancellationFee(memberId as string);
    $[2] = memberId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!memberId && (options?.enabled ?? true);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function usePaymentMethods() {
  const $ = _c(6);
  const {
    patient
  } = usePatientPortal();
  const tenantScope = useParams()?.tenant ?? "";
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== tenantScope || $[1] !== userScope) {
    t0 = portalBillingQueryKeys.paymentMethods(tenantScope, userScope);
    $[0] = tenantScope;
    $[1] = userScope;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = !!userScope;
  let t2;
  if ($[3] !== t0 || $[4] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: listPaymentMethods,
      enabled: t1,
      select: _temp
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return useQuery(t2);
}
function _temp(data) {
  return data ?? [];
}
export function useAvailableMemberships(options) {
  const $ = _c(5);
  const {
    patient
  } = usePatientPortal();
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== userScope) {
    t0 = portalBillingQueryKeys.memberships(userScope);
    $[0] = userScope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!userScope && (options?.enabled ?? true);
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: listAvailableMemberships,
      enabled: t1,
      select: _temp2
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useQuery(t2);
}
function _temp2(data) {
  return data ?? [];
}
export function useMyMemberships(options) {
  const $ = _c(5);
  const {
    patient
  } = usePatientPortal();
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== userScope) {
    t0 = portalBillingQueryKeys.myMemberships(userScope);
    $[0] = userScope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!userScope && (options?.enabled ?? true);
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: listMyMemberships,
      enabled: t1,
      select: _temp3
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useQuery(t2);
}
function _temp3(data) {
  return data ?? [];
}
export function useMembershipDetail(membershipId) {
  const $ = _c(8);
  const {
    patient
  } = usePatientPortal();
  let t0;
  if ($[0] !== membershipId) {
    t0 = portalBillingQueryKeys.membershipDetail(membershipId ?? "");
    $[0] = membershipId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== membershipId) {
    t1 = () => getMembershipDetail(membershipId);
    $[2] = membershipId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!patient?.id && !!membershipId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function usePaymentMethodConfig() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp4,
      retry: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
async function _temp4() {
  const config = await createPaymentMethodConfig();
  if (!config) {
    throw new Error("Payment method config unavailable");
  }
  return config;
}
export function useSavePaymentMethodMutation() {
  const $ = _c(3);
  const {
    patient
  } = usePatientPortal();
  const tenantScope = useParams()?.tenant ?? "";
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== tenantScope || $[1] !== userScope) {
    t0 = {
      mutationFn: _temp5,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: portalBillingQueryKeys.paymentMethods(tenantScope, userScope)
        });
      }
    };
    $[0] = tenantScope;
    $[1] = userScope;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
function _temp5(t0) {
  const {
    rainforestPaymentMethodId,
    paymentMethodConfigId
  } = t0;
  return savePaymentMethod(rainforestPaymentMethodId, paymentMethodConfigId);
}
export function useSetDefaultPaymentMethodMutation() {
  const $ = _c(3);
  const {
    patient
  } = usePatientPortal();
  const tenantScope = useParams()?.tenant ?? "";
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== tenantScope || $[1] !== userScope) {
    t0 = {
      mutationFn: _temp6,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: portalBillingQueryKeys.paymentMethods(tenantScope, userScope)
        });
      }
    };
    $[0] = tenantScope;
    $[1] = userScope;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
function _temp6(paymentMethodId) {
  return setDefaultPaymentMethod(paymentMethodId);
}
export function useDeletePaymentMethodMutation() {
  const $ = _c(3);
  const {
    patient
  } = usePatientPortal();
  const tenantScope = useParams()?.tenant ?? "";
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== tenantScope || $[1] !== userScope) {
    t0 = {
      mutationFn: _temp7,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: portalBillingQueryKeys.paymentMethods(tenantScope, userScope)
        });
      }
    };
    $[0] = tenantScope;
    $[1] = userScope;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
function _temp7(paymentMethodId) {
  return deletePaymentMethod(paymentMethodId);
}
function invalidateMyMembershipQueries(userScope: string) {
  queryClient.invalidateQueries({
    queryKey: portalBillingQueryKeys.myMemberships(userScope)
  });
  queryClient.invalidateQueries({
    queryKey: portalBenefitsQueryKeys.memberships(userScope)
  });
}
export function useCancelMembershipMutation() {
  const $ = _c(2);
  const {
    patient
  } = usePatientPortal();
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== userScope) {
    t0 = {
      mutationFn: _temp8,
      onSettled: () => {
        invalidateMyMembershipQueries(userScope);
      }
    };
    $[0] = userScope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp8(memberId) {
  return cancelMembership(memberId);
}
export function useEnrollMembershipMutation() {
  const $ = _c(2);
  const {
    patient
  } = usePatientPortal();
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== userScope) {
    t0 = {
      mutationFn: _temp9,
      onSettled: () => {
        invalidateMyMembershipQueries(userScope);
      }
    };
    $[0] = userScope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp9(t0) {
  const {
    membershipId,
    paymentMethodId,
    checkoutIdempotencyKey
  } = t0;
  return enrollInMembership(membershipId, paymentMethodId, checkoutIdempotencyKey);
}
export function useUpdateMembershipPaymentMethodMutation() {
  const $ = _c(2);
  const {
    patient
  } = usePatientPortal();
  const userScope = patient?.id ?? "";
  let t0;
  if ($[0] !== userScope) {
    t0 = {
      mutationFn: _temp10,
      onSettled: () => {
        invalidateMyMembershipQueries(userScope);
      }
    };
    $[0] = userScope;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp10(t0) {
  const {
    memberId,
    paymentMethodId
  } = t0;
  return updateMembershipPaymentMethod(memberId, paymentMethodId);
}